import React, { useCallback, useEffect, useState } from "react";
import { DatePicker, Select, Table, Tag } from "antd";
import dayjs from "dayjs";
import stockService from "../../../services/stockService";
import ReportShell from "../ReportShell";
import {
  DateRange,
  DEFAULT_RANGE,
  RANGE_PRESETS,
  rangeLabel,
  rangeParams,
} from "../reportCommon";
import {
  ExportColumn,
  exportCsv,
  exportPdf,
  PharmacyHeader,
} from "../../../utils/reportExport";

const { RangePicker } = DatePicker;

const TYPE_LABELS: Record<string, { label: string; color: string }> = {
  purchase: { label: "Purchase", color: "green" },
  sale: { label: "Sale", color: "blue" },
  sale_return: { label: "Sale return", color: "orange" },
  adjustment: { label: "Adjustment", color: "purple" },
  expired: { label: "Expired", color: "red" },
  damaged: { label: "Damaged", color: "red" },
  opening: { label: "Opening", color: "cyan" },
};

interface Props {
  pharmacy: PharmacyHeader;
}

const StockLedgerReport: React.FC<Props> = ({ pharmacy }) => {
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
  const [type, setType] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (p = page, ps = pageSize) => {
      setLoading(true);
      setError(null);
      try {
        const res: any = await stockService.getMovements({
          ...rangeParams(range),
          type,
          page: p,
          limit: ps,
        } as any);
        setRows(res.data.data || []);
        setTotal(res.data.pagination?.totalItems || 0);
      } catch (e: any) {
        setError(e.response?.data?.message || "Failed to load stock ledger");
      } finally {
        setLoading(false);
      }
    },
    [range, type, page, pageSize]
  );

  useEffect(() => {
    load(1, pageSize);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, type]);

  const exportColumns: ExportColumn[] = [
    {
      title: "Date",
      value: (r) =>
        r.createdAt ? dayjs(r.createdAt).format("DD/MM/YYYY HH:mm") : "",
    },
    {
      title: "Type",
      value: (r) => TYPE_LABELS[r.type]?.label || r.type || "",
    },
    { title: "Product", value: (r) => r.stock?.name || r.medicine?.name || "" },
    { title: "Batch", value: (r) => r.stock?.batchNumber || "" },
    { title: "Qty", value: (r) => r.quantity ?? 0 },
    { title: "Balance After", value: (r) => r.balanceAfter ?? "" },
    { title: "Reason", value: (r) => r.reason || "" },
    { title: "By", value: (r) => r.createdBy?.name || "" },
  ];

  const fetchAll = async () => {
    const res: any = await stockService.getMovements({
      ...rangeParams(range),
      type,
      page: 1,
      limit: 10000,
    } as any);
    return res.data.data || [];
  };

  const handleExportCsv = async () => {
    const all = await fetchAll();
    exportCsv("Stock Ledger", exportColumns, all, [
      `Period: ${rangeLabel(range)}`,
    ]);
  };

  const handleExportPdf = async () => {
    const all = await fetchAll();
    exportPdf({
      title: "Stock Ledger",
      pharmacy,
      subtitle: `Period: ${rangeLabel(range)}`,
      orientation: "landscape",
      columns: exportColumns,
      rows: all,
    });
  };

  return (
    <ReportShell
      title="Stock Ledger"
      loading={loading}
      error={error}
      onRefresh={() => load()}
      onReset={() => {
        setRange(DEFAULT_RANGE);
        setType(undefined);
      }}
      onExportCsv={handleExportCsv}
      onExportPdf={handleExportPdf}
      filters={
        <>
          <RangePicker
            size="small"
            value={range}
            allowClear={false}
            presets={RANGE_PRESETS}
            onChange={(v) => v && setRange(v as DateRange)}
          />
          <Select
            size="small"
            allowClear
            placeholder="Movement type"
            style={{ width: 150 }}
            value={type}
            onChange={setType}
            options={Object.entries(TYPE_LABELS).map(([value, cfg]) => ({
              value,
              label: cfg.label,
            }))}
          />
        </>
      }
    >
      <Table
        rowKey="_id"
        size="small"
        sticky
        loading={loading}
        dataSource={rows}
        scroll={{ x: 900 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `${t} movements`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
            load(p, ps);
          },
        }}
        columns={[
          {
            title: "Date",
            dataIndex: "createdAt",
            width: 140,
            render: (d: string) =>
              d ? dayjs(d).format("DD/MM/YYYY HH:mm") : "—",
          },
          {
            title: "Type",
            dataIndex: "type",
            width: 110,
            render: (t: string) => (
              <Tag color={TYPE_LABELS[t]?.color || "default"}>
                {TYPE_LABELS[t]?.label || t}
              </Tag>
            ),
          },
          {
            title: "Product",
            ellipsis: true,
            render: (_: unknown, r: any) =>
              r.stock?.name || r.medicine?.name || "—",
          },
          {
            title: "Batch",
            width: 100,
            render: (_: unknown, r: any) => r.stock?.batchNumber || "—",
          },
          {
            title: "Qty",
            dataIndex: "quantity",
            width: 70,
            align: "right" as const,
          },
          {
            title: "Balance",
            dataIndex: "balanceAfter",
            width: 80,
            align: "right" as const,
          },
          { title: "Reason", dataIndex: "reason", ellipsis: true },
          {
            title: "By",
            width: 110,
            ellipsis: true,
            render: (_: unknown, r: any) => r.createdBy?.name || "—",
          },
        ]}
      />
    </ReportShell>
  );
};

export default StockLedgerReport;
